import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ShieldOutlined } from '@mui/icons-material';
import { mfaVerify, clearError, clearMfaState, logout } from '../../redux/slices/authSlice';
import AuthLayout from './AuthLayout';

/**
 * Login-time second-factor challenge. Reached after /auth/token/ returns
 * mfa_required:true for a user who already has a confirmed authenticator.
 * Accepts a 6-digit TOTP code or a one-time recovery code.
 */
const MfaChallengePage = () => {
  const [code, setCode] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, mfaToken, isAuthenticated, user } = useSelector((state) => state.auth);

  // No challenge in progress (e.g. direct nav / refresh) — back to login.
  useEffect(() => {
    if (!mfaToken && !isAuthenticated) {
      navigate('/auth/login');
    }
  }, [mfaToken, isAuthenticated, navigate]);

  // Once verified, leave the challenge screen.
  useEffect(() => {
    if (isAuthenticated) {
      navigate(user?.must_change_password ? '/auth/change-password' : '/');
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const otp = code.trim();
    if (!otp) return;
    dispatch(mfaVerify({ mfaToken, otp }));
  };

  const handleCancel = () => {
    dispatch(clearMfaState());
    dispatch(logout());
    navigate('/auth/login');
  };

  return (
    <AuthLayout>
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          bgcolor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          p: { xs: 3.5, sm: 4.5 },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 56, height: 56, borderRadius: '50%', bgcolor: '#E3F2FD', mb: 1,
            }}
          >
            <ShieldOutlined sx={{ fontSize: 28, color: '#1565C0' }} />
          </Box>
        </Box>

        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#212529', textAlign: 'center', mb: 0.5 }}>
          Two-Factor Authentication
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: '#6c757d', textAlign: 'center', mb: 3 }}>
          Enter the 6-digit code from your authenticator app. You can also use one of your recovery codes.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 1.5 }} onClose={() => dispatch(clearError())}>
            {typeof error === 'object' ? error.detail || JSON.stringify(error) : error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            name="otp"
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isLoading}
            autoFocus
            autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric', style: { letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.2rem' } }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover fieldset': { borderColor: '#2C3E50' },
                '&.Mui-focused fieldset': { borderColor: '#2C3E50' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#2C3E50' },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || !code.trim()}
            sx={{
              py: 1.5, fontSize: '0.95rem', fontWeight: 600, textTransform: 'none',
              borderRadius: '28px', bgcolor: '#2C3E50', boxShadow: 'none',
              '&:hover': { bgcolor: '#1A242F', boxShadow: '0 4px 12px rgba(44,62,80,0.3)' },
              '&:disabled': { bgcolor: '#2C3E50', opacity: 0.7, color: '#fff' },
            }}
          >
            {isLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Verify'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2.5 }}>
            <Typography
              onClick={handleCancel}
              sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#3498db', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Cancel and sign out
            </Typography>
          </Box>
        </form>
      </Box>
    </AuthLayout>
  );
};

export default MfaChallengePage;
